import type { StakkoAppId, StakkoCreditPack } from './types';

/**
 * Všechny Stakko aplikace sdílejí jeden Firebase projekt `vibiz-ecosystem`.
 * Hodnoty jde přepsat přes VITE_* proměnné při buildu.
 */
const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

function readEnv(key: string): string | undefined {
  const value = env[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export const STAKKO_FIREBASE_CONFIG = {
  apiKey: readEnv('VITE_FIREBASE_API_KEY') || 'AIzaSyDahWnjiCHKVcOoaPTVnGlJjegmWBxStAE',
  authDomain: readEnv('VITE_FIREBASE_AUTH_DOMAIN') || 'vibiz-ecosystem.firebaseapp.com',
  projectId: readEnv('VITE_FIREBASE_PROJECT_ID') || 'vibiz-ecosystem',
  storageBucket: readEnv('VITE_FIREBASE_STORAGE_BUCKET') || 'vibiz-ecosystem.firebasestorage.app',
  messagingSenderId: readEnv('VITE_FIREBASE_MESSAGING_SENDER_ID') || '147334759326',
  appId: readEnv('VITE_FIREBASE_APP_ID') || '1:147334759326:web:331ee002d7563960d9cc8f',
};

export const STAKKO_APP_URLS: Record<StakkoAppId, string> = {
  vserozvrh: readEnv('VITE_STAKKO_URL_VSEROZVRH') || 'https://vserozvrh.cz',
  noddo: readEnv('VITE_STAKKO_URL_NODDO') || 'https://noddo.stakko.cz',
  reppo: readEnv('VITE_STAKKO_URL_REPPO') || 'https://reppo.stakko.cz',
  planno: readEnv('VITE_STAKKO_URL_PLANNO') || 'https://planno.stakko.cz',
  dukato: readEnv('VITE_STAKKO_URL_DUKATO') || 'https://dukato.stakko.cz',
  dailo: readEnv('VITE_STAKKO_URL_DAILO') || 'https://dailo.stakko.cz',
  monopoly: readEnv('VITE_STAKKO_URL_MONOPOLY') || 'https://frakto.stakko.cz',
  hub: readEnv('VITE_STAKKO_URL_HUB') || 'https://stakko.cz',
};

export const STAKKO_ACCOUNT_URL = readEnv('VITE_STAKKO_ACCOUNT_URL') || 'https://stakko.cz/ucet';
export const STAKKO_HELP_URL = readEnv('VITE_STAKKO_HELP_URL') || 'https://stakko.cz/napoveda';
export const STAKKO_SUPPORT_EMAIL =
  readEnv('VITE_STAKKO_SUPPORT_EMAIL') || 'hello@stakko.cz';
export const STAKKO_TERMS_URL = readEnv('VITE_STAKKO_TERMS_URL') || 'https://stakko.cz/podminky';
export const STAKKO_PRIVACY_URL = readEnv('VITE_STAKKO_PRIVACY_URL') || 'https://stakko.cz/soukromi';

/** Startovní bonus pro nový účet. */
export const STAKKO_WELCOME_CREDITS = 25;

/** Když je false, UI kreditů se tváří jako „pracujeme na tom“ a nejde nic koupit. */
export const STAKKO_CREDITS_ENABLED =
  (readEnv('VITE_STAKKO_CREDITS_ENABLED') || 'false').toLowerCase() === 'true';

/** Maximální počet kreditů, které lze připsat jednou klientskou akcí. */
export const STAKKO_MAX_EARN_PER_CALL = 500;

export const STAKKO_CREDIT_PACKS: StakkoCreditPack[] = [
  { id: 'credits100', credits: 100, priceCzk: 49 },
  { id: 'credits250', credits: 250, priceCzk: 99 },
  { id: 'credits500', credits: 500, priceCzk: 179, label: 'Nejoblíbenější' },
  { id: 'credits1000', credits: 1000, priceCzk: 329 },
  { id: 'credits2000', credits: 2000, priceCzk: 599, label: 'Nejvýhodnější' },
  { id: 'credits5000', credits: 5000, priceCzk: 1299 },
];

/** Stripe Payment Links. Nastav je přes VITE_STAKKO_STRIPE_* při buildu. */
export const STAKKO_STRIPE_LINKS = {
  plusMonthly:
    readEnv('VITE_STAKKO_STRIPE_PLUS_MONTHLY') ||
    'https://buy.stripe.com/fZucN7gLhekMcARcRf1Nu04',
  plusYearly:
    readEnv('VITE_STAKKO_STRIPE_PLUS_YEARLY') ||
    'https://buy.stripe.com/dRm4gBamTdgIgR76sR1Nu05',
  proMonthly:
    readEnv('VITE_STAKKO_STRIPE_PRO_MONTHLY') ||
    'https://buy.stripe.com/5kQ4gB7aH3G8fN37wV1Nu06',
  proYearly:
    readEnv('VITE_STAKKO_STRIPE_PRO_YEARLY') ||
    'https://buy.stripe.com/aFacN7bqX2C49oF7wV1Nu07',
  credits100:
    readEnv('VITE_STAKKO_STRIPE_CREDITS_100') ||
    'https://buy.stripe.com/9B600l3Yv90s7gxaJ71Nu08',
  credits250:
    readEnv('VITE_STAKKO_STRIPE_CREDITS_250') ||
    'https://buy.stripe.com/4gMbJ366D0tWgR73gF1Nu09',
  credits500:
    readEnv('VITE_STAKKO_STRIPE_CREDITS_500') ||
    'https://buy.stripe.com/bJe14p2UrccE7gxeZn1Nu0a',
  credits1000:
    readEnv('VITE_STAKKO_STRIPE_CREDITS_1000') ||
    'https://buy.stripe.com/aFa14pfHddgIfN318x1Nu0b',
  credits2000:
    readEnv('VITE_STAKKO_STRIPE_CREDITS_2000') ||
    'https://buy.stripe.com/9B6eVfdz5b8A30hbNb1Nu0c',
  credits5000:
    readEnv('VITE_STAKKO_STRIPE_CREDITS_5000') ||
    'https://buy.stripe.com/dRmfZjdz590s58p5oN1Nu0d',
};

export const STAKKO_STRIPE_PORTAL_URL =
  readEnv('VITE_STAKKO_STRIPE_PORTAL_URL') ||
  'https://billing.stripe.com/p/login/28EeVf3Yv7Wo9oFcRf1Nu00';

export type StakkoCheckoutKind =
  | 'plusMonthly'
  | 'plusYearly'
  | 'proMonthly'
  | 'proYearly'
  | 'credits100'
  | 'credits250'
  | 'credits500'
  | 'credits1000'
  | 'credits2000'
  | 'credits5000';

export const STAKKO_PLAN_OFFERS = [
  {
    id: 'plus' as const,
    name: 'Student+',
    monthlyCzk: 59,
    yearlyCzk: 490,
    monthlyKind: 'plusMonthly' as const,
    yearlyKind: 'plusYearly' as const,
    perks: ['Všerozvrh bez limitů', 'Noddo a poznámky', 'Kartičky', '100 kreditů / měsíc'],
  },
  {
    id: 'pro' as const,
    name: 'Student Pro',
    monthlyCzk: 119,
    yearlyCzk: 990,
    monthlyKind: 'proMonthly' as const,
    yearlyKind: 'proYearly' as const,
    perks: ['Vše ze Student+', 'Reppo a Dukato', 'AI shrnutí a kartičky z PDF', '400 kreditů / měsíc'],
  },
];

/** Co je součástí jednotlivých plánů. */
export const STAKKO_PLAN_APPS: Record<'plus' | 'pro', StakkoAppId[]> = {
  plus: ['vserozvrh', 'noddo', 'planno'],
  pro: ['vserozvrh', 'noddo', 'planno', 'reppo', 'dukato', 'dailo', 'monopoly'],
};

export const STAKKO_PLAN_LABELS: Record<'free' | 'plus' | 'pro', string> = {
  free: 'Free',
  plus: 'Student+',
  pro: 'Student Pro',
};
